import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../utils/api';
import { toast } from 'sonner';
import { Building2, Plus, LogOut, Mail, Settings as SettingsIcon, Search, Loader2, Eye, Trash2, CreditCard, Edit, Filter } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const Dashboard = ({ setAuth }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [clientType, setClientType] = useState('Contract Support');
  const [clientTypes, setClientTypes] = useState(['Contract Support', 'PAYG Support', 'New Prospect', 'Archived']);
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchClientTypes();
  }, []);

  const fetchClientTypes = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data.client_types && response.data.client_types.length > 0) {
        setClientTypes(response.data.client_types);
      }
    } catch (error) {
      // Use default types if settings fetch fails
      console.log('Using default client types');
    }
  };

  useEffect(() => {
    let filtered = clients;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (client) =>
          client.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.company_number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((client) => client.client_type === typeFilter);
    }
    
    setFilteredClients(filtered);
  }, [searchTerm, typeFilter, clients]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const searchCompanies = async (query) => {
    if (query.length < 3) {
      setCompanySearchResults([]);
      setShowCompanyDropdown(false);
      return;
    }

    setSearchingCompanies(true);
    try {
      const response = await api.get(`/companies-house/search?q=${encodeURIComponent(query)}`);
      setCompanySearchResults(response.data.items || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      setCompanySearchResults([]);
    } finally {
      setSearchingCompanies(false);
    }
  };

  const selectCompany = (company) => {
    setCompanyName(company.company_name);
    setCompanyNumber(company.company_number);
    setShowCompanyDropdown(false);
    setCompanySearchResults([]);
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setAdding(true);

    try {
      const clientResponse = await api.post('/clients', {
        company_name: companyName,
        company_number: companyNumber,
        client_type: clientType,
      });
      
      // If client type is "New Prospect", also create a prospect record
      if (clientType === 'New Prospect') {
        try {
          await api.post('/prospects', {
            company_name: companyName,
            company_number: companyNumber,
            status: 'new', // Active prospect status
            notes: `Auto-created from client with type "New Prospect"`
            // Note: NOT setting converted_to_client_id so it stays visible in prospects list
          });
          toast.success('Client and prospect record created successfully!');
        } catch (prospectError) {
          console.error('Failed to create prospect:', prospectError);
          toast.success('Client added successfully (Note: Prospect record creation failed)');
        }
      } else {
        toast.success('Client added successfully!');
      }
      
      setShowAddDialog(false);
      setCompanyName('');
      setCompanyNumber('');
      setClientType('Contract Support');
      setCompanySearchResults([]);
      setShowCompanyDropdown(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    } finally {
      setAdding(false);
    }
  };

  const openEditDialog = (client) => {
    setClientToEdit(client);
    setCompanyName(client.company_name);
    setCompanyNumber(client.company_number);
    setClientType(client.client_type || 'Contract Support');
    setShowEditDialog(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    if (!clientToEdit) return;
    
    setUpdating(true);

    try {
      await api.put(`/clients/${clientToEdit.id}`, {
        company_name: companyName,
        company_number: companyNumber,
        client_type: clientType,
      });
      toast.success('Client updated successfully!');
      setShowEditDialog(false);
      setClientToEdit(null);
      setCompanyName('');
      setCompanyNumber('');
      setClientType('Contract Support');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    try {
      await api.delete(`/clients/${clientToDelete.id}`);
      toast.success('Client deleted successfully!');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <>
    <div className="p-0 sm:p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Clients</h2>
              <p className="text-slate-600 mt-1">Manage your client portfolio</p>
            </div>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
              data-testid="add-client-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-4xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by company name or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-11 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500 bg-white shadow-sm"
                data-testid="search-input"
              />
            </div>
            <div className="relative sm:w-64">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="pl-11 h-11 border-slate-300 bg-white shadow-sm">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {clientTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="shadow-sm border-slate-200" data-testid="empty-state">
            <CardContent className="py-20 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {searchTerm ? 'No clients found' : 'No clients yet'}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchTerm ? 'Try a different search term' : 'Get started by adding your first client'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Client
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredClients.map((client) => (
              <Card key={client.id} className="hover:shadow-lg border-slate-200 bg-white" data-testid={`client-card-${client.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg font-semibold text-slate-800 line-clamp-1">
                        {client.company_name}
                      </CardTitle>
                      <CardDescription className="text-slate-500">#{client.company_number}</CardDescription>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {client.client_type || 'N/A'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4 text-sm">
                    {client.website && (
                      <p className="text-slate-600 line-clamp-1">
                        <span className="font-medium">Website:</span> {client.website}
                      </p>
                    )}
                    {client.email && (
                      <p className="text-slate-600 line-clamp-1">
                        <span className="font-medium">Email:</span> {client.email}
                      </p>
                    )}
                    {!client.website && !client.email && (
                      <p className="text-slate-400 italic">No additional details</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      data-testid={`view-client-${client.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(client)}
                      className="border-slate-300 hover:bg-slate-50"
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setClientToDelete(client);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Add Client Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Add New Client</DialogTitle>
            <DialogDescription>Enter the company details to add a new client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddClient}>
            <div className="space-y-4 py-4">
              <div className="space-y-2 relative">
                <Label htmlFor="company-name">Company Name</Label>
                <div className="relative">
                  <Input
                    id="company-name"
                    data-testid="company-name-input"
                    placeholder="Start typing company name..."
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      searchCompanies(e.target.value);
                    }}
                    onFocus={() => companySearchResults.length > 0 && setShowCompanyDropdown(true)}
                    required
                    className="border-slate-300"
                    autoComplete="off"
                  />
                  {searchingCompanies && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                  )}
                </div>
                {showCompanyDropdown && companySearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {companySearchResults.map((company, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectCompany(company)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-800">{company.company_name}</div>
                        <div className="text-sm text-slate-500">
                          #{company.company_number} • {company.company_status}
                        </div>
                        {company.address_snippet && (
                          <div className="text-xs text-slate-400 mt-1">{company.address_snippet}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-number">Company Number</Label>
                <Input
                  id="company-number"
                  data-testid="company-number-input"
                  placeholder="Auto-filled when you select a company"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-type">Client Type</Label>
                <Select value={clientType} onValueChange={setClientType}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} disabled={adding}>
                Cancel
              </Button>
              <Button type="submit" disabled={adding} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-client-button">
                {adding ? 'Adding...' : 'Add Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Edit Client</DialogTitle>
            <DialogDescription>Update the client details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditClient}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-company-name">Company Name</Label>
                <Input
                  id="edit-company-name"
                  data-testid="edit-company-name-input"
                  placeholder="Company name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-company-number">Company Number</Label>
                <Input
                  id="edit-company-number"
                  data-testid="edit-company-number-input"
                  placeholder="Company number"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-client-type">Client Type</Label>
                <Select value={clientType} onValueChange={setClientType}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                  setClientToEdit(null);
                  setCompanyName('');
                  setCompanyNumber('');
                  setClientType('Contract Support');
                }} 
                disabled={updating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updating} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-button">
                {updating ? 'Updating...' : 'Update Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{clientToDelete?.company_name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Dashboard;
